<template>
    <v-container class="pa-5" fluid>
        <ImagePreviewDialog
            :is-open="imagePreviewDialog"
            :file-preview-uri="filePreviewURI"
            @on-close="closeImagePreviewDialog"
        />
        <UpdateReportAmountDialog
            :is-open="reportAmountDialog"
            :report-data="report"
            @on-save="saveReportAmountData"
            @on-close="closeReportAmountDialog"
        />
        <WorkspaceAccidentsListDialog
            :is-open="workspaceAccidentsListDialog"
            :workspace-accidents-list-data="unsolvedAccidents"
            @on-close="closeWorkspaceAccidentsListDialog"
        />
        <ControlCodeDialog
            v-if="isOrder || jobFromSchedule || jobFactoryBase"
            :is-open="controlCodeDialog"
            :report-id="report.id"
            :order-number="report.objectNumber"
            @on-next-scan="getCodeCompliancesList"
            @on-close="closeControlCodeDialog"
        />

        <page-title>Raportowanie / Szczegóły raportu</page-title>

        <v-row>
            <v-col cols="12" xl="10" offset-xl="1">
                <v-container>
                    <v-row>
                        <v-col cols="12">
                            <v-badge
                                :content="unsolvedAccidentsAmount"
                                :value="unsolvedAccidentsAmount"
                                :color="unsolvedAccidentsAmount === '0' ? 'success' : 'error'"
                                overlap
                            >
                                <v-btn
                                    color="primary"
                                    outlined
                                    @click="showWorkspaceAccidentsListDialog"
                                >
                                    Awarie w toku
                                </v-btn>
                            </v-badge>
                            <v-btn
                                v-if="printMethod"
                                color="primary"
                                class="ml-4"
                            >
                                Tryb druku: {{ printMethod }}
                            </v-btn>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col cols="12">
                            <ReportTimerTracker
                                :report="report"
                                :task-name="taskName"
                                :delay="delay"
                                :duration="duration"
                                :time-without-limit="timeWithoutLimit"
                                :additional-actions-report-process-code-production-site="additionalActionsReportProcessCodeProductionSite"
                                @toggle-select-all-report-process-code="toggleSelectAllReportProcessCode"
                                @update-select-all-report-process-code="updateSelectAllReportProcessCode"
                            />
                        </v-col>
                        <v-col v-if="!isProcessReport" cols="12" lg="6">
                            <v-container class="pa-0">
                                <v-row>
                                    <v-col cols="12">
                                        <ReportParametersData
                                            :report-data="report"
                                            @edit-report-amount="showEditReportAmountDialog"
                                        />
                                        <v-card
                                            v-if="isOrder || jobFromSchedule || jobFactoryBase"
                                            :loading="codeCompliancesListLoading"
                                        >
                                            <v-toolbar flat dense color="white lighten-2">
                                                <v-toolbar-title>Kontrola kodu</v-toolbar-title>
                                            </v-toolbar>
                                            <v-divider />
                                            <v-container fluid class="pa-0">
                                                <v-row>
                                                    <v-col cols="12">
                                                        <v-data-table
                                                            :headers="controlCodeHeaders"
                                                            :items="controlCodeList"
                                                            no-data-text="Brak zeskanowanych kodów"
                                                            :mobile-breakpoint="0"
                                                            :item-per-page="10"
                                                            :hide-default-footer="controlCodeList.length <= 10"
                                                        />
                                                    </v-col>
                                                    <v-col class="text-center">
                                                        <v-btn
                                                            color="primary"
                                                            data-sentry-label="Start"
                                                            @click="showControlCodeDialog"
                                                        >
                                                            Start
                                                        </v-btn>
                                                    </v-col>
                                                </v-row>
                                            </v-container>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col v-if="!isProcessReport && !isReportFinished && isOffsetPrintSite" cols="12" lg="6">
                            <v-container class="pa-0">
                                <v-row align="center">
                                    <v-col v-if="!isProcessReport && !isReportFinished" cols="12">
                                        <v-stepper v-model="stepper">
                                            <v-progress-linear
                                                color="blue"
                                                indeterminate
                                                :active="orderLoading"
                                                absolute
                                                height="3"
                                            />
                                            <v-overlay :value="orderLoading" opacity="0" absolute />

                                            <v-stepper-header class="no-shadow">
                                                <v-stepper-step step="1" :complete="stepper > 1" color="primary">
                                                    Raport strona: {{ report.objectDetail.printSideType ? report.objectDetail.printSideType : 'Nieznane' }}
                                                </v-stepper-step>
                                            </v-stepper-header>

                                            <v-stepper-items>
                                                <v-stepper-content step="1">
                                                    <ReportData
                                                        :report-data="report"
                                                        :allows-sending-orders="allowsSendingOrders"
                                                        :allows-report-excess-amount="allowsReportExcessAmount"
                                                        :route-parent-name="routeParentName"
                                                        :machine-stuff-amount-data="machineStuffAmount"
                                                        :max-report-amount-data="maxReportAmount"
                                                        :currently-reported-quantity-data="currentlyReportedQuantity"
                                                        :delay="delay"
                                                        :time-without-limit="timeWithoutLimit"
                                                        :is-from-reporting="isFromReporting"
                                                        :workspace-id="workspaceId"
                                                        :additional-action-process-production-site="additionalActionsProcessProductionSite"
                                                        :additional-action-report-process-code-production-site="additionalActionsReportProcessCodeProductionSite"
                                                        :is-pallet-number-required="isPalletNumberRequired"
                                                        :is-next-step-visible="false"
                                                        @set-duration="setDuration"
                                                        @update-renew-report="updateRenewReport"
                                                        @update-end-make-ready-report="updateEndMakeReadyReport"
                                                        @order-loading="setOrderLoading"
                                                        @show-next-step="showNextStep"
                                                    />
                                                </v-stepper-content>
                                            </v-stepper-items>
                                        </v-stepper>
                                    </v-col>
                                    <v-col v-if="additionalActionsProcessProductionSite.length > 0" cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <v-data-table
                                                    :headers="additionalActionsProcessProductionSiteHeaders"
                                                    :items="additionalActionsProcessProductionSite"
                                                    disable-pagination
                                                    hide-default-footer
                                                    :mobile-breakpoint="0"
                                                    show-select
                                                    name="additionalActionsProcessProductionSiteData"
                                                >
                                                    <template #header.data-table-select>
                                                        <v-simple-checkbox v-if="!isReportFinished" v-model="selectAll" :ripple="false" @input="toggleSelectAll($event)" />
                                                    </template>
                                                    <template #item.data-table-select="{item}">
                                                        <v-simple-checkbox v-model="item.isSelected" :disabled="isReportFinished" :ripple="false" @input="updateSelectAll()" />
                                                    </template>
                                                </v-data-table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                                <v-card v-if="!isProcessReport && !isReportFinished">
                                    <v-toolbar flat dense color="white lighten-2">
                                        <v-toolbar-title>Podgląd</v-toolbar-title>
                                    </v-toolbar>

                                    <v-divider />

                                    <v-card-text>
                                        <v-container class="pa-0">
                                            <v-row>
                                                <template v-if="hasPreviews">
                                                    <v-col v-for="(components, key, index) in reportPreviews" :key="index">
                                                        <h3 class="text-subtitle-1 my-2">
                                                            {{ key }}
                                                        </h3>
                                                        <v-row>
                                                            <v-col
                                                                v-for="(image, previewIndex) in components"
                                                                :key="previewIndex"
                                                                cols="6"
                                                                @click="showFilePreviewDialog(image)"
                                                            >
                                                                <div
                                                                    class="fill-width text-h6 d-flex align-center justify-center success white--text"
                                                                >
                                                                    {{ image.side == 1 ? 'A' : 'B' }}
                                                                </div>
                                                                <v-img
                                                                    :src="taskPreviewURI(image)"
                                                                    max-height="350"
                                                                    contain
                                                                >
                                                                    <template #placeholder>
                                                                        <v-row
                                                                            class="fill-height ma-0"
                                                                            align="center"
                                                                            justify="center"
                                                                        >
                                                                            <v-progress-circular indeterminate color="primary" width="2" />
                                                                        </v-row>
                                                                    </template>
                                                                </v-img>
                                                            </v-col>
                                                        </v-row>
                                                    </v-col>
                                                </template>
                                                <v-col v-else cols="12">
                                                    <v-alert
                                                        type="info"
                                                        text
                                                        border="left"
                                                    >
                                                        Brak podglądów.
                                                    </v-alert>
                                                </v-col>
                                            </v-row>
                                        </v-container>
                                    </v-card-text>
                                </v-card>
                            </v-container>
                        </v-col>
                        <v-col v-if="!isProcessReport && !isReportFinished && !isOffsetPrintSite" cols="12" lg="6">
                            <v-container class="pa-0">
                                <v-row align="center">
                                    <v-col v-if="!isProcessReport && !isReportFinished" cols="12">
                                        <v-stepper v-model="stepper">
                                            <v-progress-linear
                                                color="blue"
                                                indeterminate
                                                :active="orderLoading"
                                                absolute
                                                height="3"
                                            />
                                            <v-overlay :value="orderLoading" opacity="0" absolute />

                                            <v-stepper-header class="no-shadow">
                                                <v-stepper-step step="1" :complete="stepper > 1" :color="stepper > 1 ? 'success' : 'primary'">
                                                    Raport
                                                </v-stepper-step>

                                                <v-divider />
                                                
                                                <v-stepper-step v-if="isJob" step="2" :complete="stepper > 2" :color="stepper > 2 ? 'success' : 'primary'">
                                                    Zamówienia
                                                </v-stepper-step>

                                                <v-divider />

                                                <v-stepper-step 
                                                    :step="getStep" 
                                                    :complete="stepper > getStep" 
                                                    :color="stepper > getStep ? 'success' : 'primary'"
                                                >
                                                    Pakowanie
                                                </v-stepper-step>
                                            </v-stepper-header>

                                            <v-stepper-items>
                                                <v-stepper-content step="1">
                                                    <ReportData
                                                        :report-data="report"
                                                        :allows-sending-orders="allowsSendingOrders"
                                                        :allows-report-excess-amount="allowsReportExcessAmount"
                                                        :route-parent-name="routeParentName"
                                                        :machine-stuff-amount-data="machineStuffAmount"
                                                        :max-report-amount-data="maxReportAmount"
                                                        :currently-reported-quantity-data="currentlyReportedQuantity"
                                                        :delay="delay"
                                                        :time-without-limit="timeWithoutLimit"
                                                        :is-from-reporting="isFromReporting"
                                                        :workspace-id="workspaceId"
                                                        :additional-action-process-production-site="additionalActionsProcessProductionSite"
                                                        :additional-action-report-process-code-production-site="additionalActionsReportProcessCodeProductionSite"
                                                        :is-pallet-number-required="isPalletNumberRequired"
                                                        :is-next-step-visible="true"
                                                        @set-duration="setDuration"
                                                        @update-renew-report="updateRenewReport"
                                                        @update-end-make-ready-report="updateEndMakeReadyReport"
                                                        @order-loading="setOrderLoading"
                                                        @show-next-step="showNextStep"
                                                    />
                                                </v-stepper-content>
 
                                                <v-stepper-content v-if="isJob" step="2">
                                                    <OrderData 
                                                        :report-data="report"
                                                        @send-order="sendOrder(orderNumber)"
                                                        @order-loading="() => orderLoading = false"
                                                        @show-previous-step="showPreviousStep"
                                                        @show-next-step="showNextStep"
                                                    />
                                                </v-stepper-content>

                                                <v-stepper-content :step="getStep">
                                                    <SendingData
                                                        :report-data="report"
                                                        :couriers="couriers"
                                                        :workspace-id="workspaceId"
                                                        @order-loading="() => orderLoading = false"
                                                        @show-previous-step="showPreviousStep"
                                                    />
                                                </v-stepper-content>
                                            </v-stepper-items>
                                        </v-stepper>
                                    </v-col>
                                    <v-col v-if="additionalActionsProcessProductionSite.length > 0" cols="12">
                                        <v-card>
                                            <v-card-text>
                                                <v-data-table
                                                    :headers="additionalActionsProcessProductionSiteHeaders"
                                                    :items="additionalActionsProcessProductionSite"
                                                    disable-pagination
                                                    hide-default-footer
                                                    :mobile-breakpoint="0"
                                                    show-select
                                                    name="additionalActionsProcessProductionSiteData"
                                                >
                                                    <template #header.data-table-select>
                                                        <v-simple-checkbox v-if="!isReportFinished" v-model="selectAll" :ripple="false" @input="toggleSelectAll($event)" />
                                                    </template>
                                                    <template #item.data-table-select="{item}">
                                                        <v-simple-checkbox v-model="item.isSelected" :disabled="isReportFinished" :ripple="false" @input="updateSelectAll()" />
                                                    </template>
                                                </v-data-table>
                                            </v-card-text>
                                        </v-card>
                                    </v-col>
                                </v-row>
                            </v-container>
                        </v-col>
                        <v-col v-if="!isProcessReport && !isReportFinished && !isOffsetPrintSite" cols="12">
                            <v-card>
                                <v-toolbar flat dense color="white lighten-2">
                                    <v-toolbar-title>Podgląd</v-toolbar-title>
                                </v-toolbar>

                                <v-divider />

                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row dense>
                                            <template v-if="hasPreviews">
                                                <v-col v-for="(components, key, index) in previews" :key="index" cols="6">
                                                    <h3 class="text-subtitle-1 my-2">
                                                        {{ key }}
                                                    </h3>
                                                    <v-row>
                                                        <v-col cols="11">
                                                            <v-container>
                                                                <v-carousel
                                                                    v-model="carouselIndexes[index]"
                                                                    height="auto"
                                                                    hide-delimiters
                                                                    :show-arrows="false"
                                                                    :continuous="false"
                                                                    :dark="$vuetify.theme.dark"
                                                                    :light="!$vuetify.theme.dark"
                                                                    :touchless="true"
                                                                >
                                                                    <v-carousel-item
                                                                        v-for="(image, previewIndex) in components.filter((item, index2) => index2 % 2 == 0)"
                                                                        :key="previewIndex"
                                                                    >
                                                                        <v-container>
                                                                            <v-row justify="space-around">
                                                                                <v-icon :disabled="!(previewIndex > 0)" @click="prev(index)">
                                                                                    mdi-chevron-left-circle
                                                                                </v-icon>
                                                                                <template v-if="image.pageNumber">
                                                                                    {{ 'Strona ' + image.pageNumber }}
                                                                                </template>
                                                                                <v-icon :disabled="!(previewIndex * 2 + 1 < components.length - 1)" @click="next(index)">
                                                                                    mdi-chevron-right-circle
                                                                                </v-icon>
                                                                            </v-row>
                                                                            <v-row dense>
                                                                                <v-col cols="6">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(image)"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(image)"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex * 2 + 1 < components.length" cols="6">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(components[previewIndex * 2 + 1])"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(components[previewIndex * 2 + 1])"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-container>
                                                                    </v-carousel-item>
                                                                </v-carousel>
                                                            </v-container>
                                                        </v-col>
                                                        <v-col v-if="index % 2 == 0" cols="1">
                                                            <v-divider
                                                                vertical
                                                            />
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                            </template>
                                            <v-col v-else cols="12">
                                                <v-alert
                                                    type="info"
                                                    text
                                                    border="left"
                                                >
                                                    Brak podglądów.
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col v-if="!isProcessReport && !isReportFinished && !isOffsetPrintSite" cols="12">
                            <v-card>
                                <v-toolbar flat dense color="white lighten-2">
                                    <v-toolbar-title>Makieta</v-toolbar-title>
                                </v-toolbar>
                                <v-divider />
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row dense>
                                            <template v-if="hasMockupPreviews">
                                                <v-col v-for="(components, key, index) in mockupPreviews" :key="index" cols="12">
                                                    <v-row>
                                                        <v-col cols="11">
                                                            <v-container>
                                                                <v-carousel
                                                                    v-model="carouselMockupsIndexes[index]"
                                                                    height="auto"
                                                                    hide-delimiters
                                                                    :show-arrows="false"
                                                                    :continuous="false"
                                                                    :dark="$vuetify.theme.dark"
                                                                    :light="!$vuetify.theme.dark"
                                                                    :touchless="true"
                                                                >
                                                                    <v-carousel-item
                                                                        v-for="(image, previewIndex) in components"
                                                                        :key="previewIndex"
                                                                    >
                                                                        <v-container>
                                                                            <v-row justify="space-between">
                                                                                <v-icon :disabled="!(previewIndex > 0)" @click="prev(index, true)">
                                                                                    mdi-chevron-left-circle
                                                                                </v-icon>
                                                                                <v-icon :disabled="!(previewIndex + 3 < components.length - 1)" @click="next(index, true)">
                                                                                    mdi-chevron-right-circle
                                                                                </v-icon>
                                                                            </v-row>
                                                                            <v-row dense>
                                                                                <v-col cols="3">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(image)"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(image)"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 1 < components.length" cols="3">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(components[previewIndex + 1])"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(components[previewIndex + 1])"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 2 < components.length" cols="3">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(components[previewIndex + 2])"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(components[previewIndex + 2])"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                                <v-col v-if="previewIndex + 3 < components.length" cols="3">
                                                                                    <v-img
                                                                                        :src="taskPreviewURI(components[previewIndex + 3])"
                                                                                        max-height="350"
                                                                                        contain
                                                                                        @click="showFilePreviewDialog(components[previewIndex + 3])"
                                                                                    >
                                                                                        <template #placeholder>
                                                                                            <v-row
                                                                                                class="fill-height ma-0"
                                                                                                align="center"
                                                                                                justify="center"
                                                                                            >
                                                                                                <v-progress-circular indeterminate color="primary" width="2" />
                                                                                            </v-row>
                                                                                        </template>
                                                                                    </v-img>
                                                                                </v-col>
                                                                            </v-row>
                                                                        </v-container>
                                                                    </v-carousel-item>
                                                                </v-carousel>
                                                            </v-container>
                                                        </v-col>
                                                    </v-row>
                                                </v-col>
                                            </template>
                                            <v-col v-else cols="12">
                                                <v-alert
                                                    type="info"
                                                    text
                                                    border="left"
                                                >
                                                    Brak podglądów makiet.
                                                </v-alert>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>

                        <NextScheduledTasks 
                            v-if="!isProcessReport && !isReportFinished && !isReportStopped" 
                            :limit="isOffsetPrintSite ? 3 : 1"
                            :is-printing="isOffsetPrintSite"
                            cols="12" 
                        />

                        <v-col v-if="isReportFinished && !isProcessReport && !isSendingsViewActive" cols="12">
                            <v-card>
                                <v-toolbar flat dense color="white lighten-2">
                                    <v-toolbar-title>Lista zdarzeń</v-toolbar-title>
                                </v-toolbar>
                                <v-divider />
                                <v-card-text>
                                    <v-container class="pa-0">
                                        <v-row>
                                            <v-col cols="12">
                                                <v-data-table
                                                    :headers="reportCommentHeaders"
                                                    :items="report.comments"
                                                    :mobile-breakpoint="0"
                                                    :items-per-page="5"
                                                    :page.sync="currentPaginationPage"
                                                    :footer-props="{
                                                        itemsPerPageText: 'Komentarzy na stronie',
                                                        showFirstLastPage: false,
                                                    }"
                                                    name="commentsData"
                                                >
                                                    <template #item.codeType="{item}">
                                                        <template v-if="item.commentCode.codeType">
                                                            {{ item.commentCode.codeType.name }}
                                                        </template>
                                                        <template v-else>
                                                            -
                                                        </template>
                                                    </template>
                                                    <template #item.description="{item}">
                                                        {{ item.commentCode.description }}
                                                    </template>
                                                    <template #item.createdDateTime="{item}">
                                                        {{ formatDate(item.createdDateTime) }}
                                                    </template>
                                                </v-data-table>
                                            </v-col>
                                        </v-row>
                                    </v-container>
                                </v-card-text>
                            </v-card>
                        </v-col>
                        <v-col>
                            <v-row>
                                <v-col cols="auto" class="mr-auto">
                                    <v-btn
                                        v-if="isReportFinished"
                                        :color="!isReportFinished && isProcessReport ? 'info' : 'error'"
                                        class="mr-4"
                                        :disabled="stopTrackingLoading"
                                        data-sentry-label="Wstecz"
                                        @click="close"
                                    >
                                        Wstecz
                                    </v-btn>
                                    <v-btn
                                        v-if="!isReportFinished && isProcessReport"
                                        :loading="stopTrackingLoading"
                                        color="error"
                                        data-sentry-label="Zakończ"
                                        @click="stopTracking"
                                    >
                                        Zakończ
                                    </v-btn>
                                </v-col>
                            </v-row>
                        </v-col>
                    </v-row>
                </v-container>
            </v-col>
        </v-row>
    </v-container>
</template>

<script>
import store from '@/./store'
import PageTitle from '@/components/common/PageTitle'
import ImagePreviewDialog from '@/components/common/ImagePreviewDialog'
import UpdateReportAmountDialog from '../ReportsShiftSummaries/UpdateReportAmountDialog'
import WorkspaceAccidentsListDialog from '../Reporting/WorkspaceAccidentsListDialog'
import ControlCodeDialog from './ControlCodeDialog'

import ReportData from './ReportData'
import SendingData from './SendingData'
import OrderData from './OrderData'
import NextScheduledTasks from './NextScheduledTasks'
import ReportParametersData from '../Common/ReportParametersData'
import ReportTimerTracker from './ReportTimerTracker'

import dateFormatter from '@/mixins/dateFormatter'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'
import pagination from '@/mixins/dataTablePagination'

import { errorHandler } from '@/utils/errorHandler'
import { groupBy } from 'lodash'
import { apiConfig } from '@/config'
import { EventBus } from '@/utils/eventBus'

export default {
    name: 'Report',
    components: {
        PageTitle,
        ReportData,
        SendingData,
        OrderData,
        ImagePreviewDialog,
        UpdateReportAmountDialog,
        WorkspaceAccidentsListDialog,
        ControlCodeDialog,
        NextScheduledTasks,
        ReportParametersData,
        ReportTimerTracker,
    },
    filters: {
        lowercase(value) {
            return value.toString().toLowerCase()
        }
    },
    mixins: [
        dateFormatter,
        parse,
        tooltipDelay,
        pagination,
    ],
    beforeRouteEnter(to, from, next) {
        Promise.all([
            store.dispatch('reports/getTaskNames'),
            store.dispatch('shipments/sendings/getCouriers')
        ])
            .then(responses => {
                const workspace = JSON.parse(localStorage.getItem('workspace'))

                if (typeof to.params.report !== 'undefined') {
                    if (to.params.report.objectType !== 3) {
                        store.dispatch('reports/getAdditionalActionProcessProductionSite', {
                            productionSiteId: to.params.report.productionSiteId,
                            processId: to.params.report.process,
                            query: `?reportId=${to.params.report.id}`
                        }).then(resp => {
                            next(vm => {
                                vm.redirectedFrom = from.name
                                vm.taskNames = responses[0].data.results
                                vm.report = to.params.report
                                if (!vm.isReportFinished) vm.setDuration()
                                vm.report.reportAmount = vm.isReportStopped || vm.isReportFinished ? to.params.report.reportAmount : to.params.report.leftAmount
                                vm.maxReportAmount = to.params.report.leftAmount
                                vm.taskName = vm.taskNames.find(item => item.id === to.params.report.objectType).name
                                vm.routeParentName = to.matched.slice(-1)[0]?.parent.name
                                vm.machineStuffAmount = 1
                                vm.remainingTime = to.params.report.remainingTime
                                vm.currentlyReportedQuantity = to.params.report.reportAmount
                                vm.orderNumber = to.params.report.objectNumber
                                vm.allowsSendingOrders = workspace.allowsSendingOrders
                                vm.allowsReportExcessAmount = workspace.allowsReportExcessAmount
                                vm.isPalletNumberRequired = workspace.isPalletNumberRequired
                                vm.couriers = responses[1].data.results
                                vm.workspaceId = workspace.workspaceId
                                vm.additionalActionsProcessProductionSite = resp.data.results.slice(0, resp.data.results.length)
                                vm.carouselIndexes = new Array(groupBy(vm.report.previews, 'type').length).fill(0)
                                vm.carouselMockupIndexes = new Array(groupBy(vm.report.mockupPreviews, 'type').length).fill(0)
                            })
                        })
                            .catch(error => {
                                next(error)
                                errorHandler(error)
                            })
                    } else {
                        next(vm => {
                            vm.redirectedFrom = from.name
                            vm.taskNames = responses[0].data.results
                            vm.report = to.params.report
                            if (!vm.isReportFinished) vm.setDuration()
                            vm.report.reportAmount = vm.isReportStopped || vm.isReportFinished ? to.params.report.reportAmount : to.params.report.leftAmount
                            vm.maxReportAmount = to.params.report.leftAmount
                            vm.taskName = vm.taskNames.find(item => item.id === to.params.report.objectType).name
                            vm.routeParentName = to.matched.slice(-1)[0]?.parent.name
                            vm.machineStuffAmount = 1
                            vm.remainingTime = to.params.report.remainingTime
                            vm.currentlyReportedQuantity = to.params.report.reportAmount
                            vm.orderNumber = to.params.report.objectNumber
                            vm.allowsSendingOrders = workspace.allowsSendingOrders
                            vm.allowsReportExcessAmount = workspace.allowsReportExcessAmount
                            vm.couriers = responses[1].data.results
                            vm.workspaceId = workspace.workspaceId
                            vm.carouselIndexes = new Array(groupBy(vm.report.previews, 'type').length).fill(0)
                            vm.carouselMockupIndexes = new Array(groupBy(vm.report.mockupPreviews, 'type').length).fill(0)
                        })
                    }
                } else {
                    store.dispatch('reports/getReport', to.params.id)
                        .then(response => {
                            if (response.objectType !== 3) {
                                store.dispatch('reports/getAdditionalActionProcessProductionSite', {
                                    productionSiteId: response.productionSiteId,
                                    processId: response.process,
                                    query: `?reportId=${response.id}`
                                }).then(resp => {
                                    next(vm => {
                                        vm.redirectedFrom = from.name
                                        vm.taskNames = responses[0].data.results
                                        vm.report = response
                                        if (!vm.isReportFinished) vm.setDuration()
                                        vm.report.reportAmount = vm.isReportStopped || vm.isReportFinished ? response.reportAmount : response.leftAmount
                                        vm.machineStuffAmount = 1
                                        vm.maxReportAmount = response.leftAmount
                                        vm.taskName = vm.taskNames.find(item => item.id === response.objectType).name
                                        vm.routeParentName = to.matched.slice(-1)[0]?.parent.name
                                        vm.remainingTime = response.remainingTime
                                        vm.currentlyReportedQuantity = response.reportAmount
                                        vm.orderNumber = response.objectNumber
                                        vm.allowsSendingOrders = workspace ? workspace.allowsSendingOrders : false
                                        vm.allowsReportExcessAmount = workspace ? workspace.allowsReportExcessAmount : false
                                        vm.isPalletNumberRequired = workspace ? workspace.isPalletNumberRequired: false
                                        vm.couriers = responses[1].data.results
                                        vm.workspaceId = workspace ? workspace.workspaceId : null
                                        vm.additionalActionsProcessProductionSite = resp.data.results.slice(resp.data.results)
                                        vm.carouselIndexes = new Array(groupBy(vm.report.previews, 'type').length).fill(0)
                                        vm.carouselMockupIndexes = new Array(groupBy(vm.report.mockupPreviews, 'type').length).fill(0)
                                    })
                                })
                                    .catch(error => {
                                        next(error)
                                        errorHandler(error)
                                    })
                            } else {
                                store.dispatch('reports/getAdditionalActionReportProcessCodeProductionSite', {
                                    productionSiteId: response.productionSiteId,
                                    processCodeId: response.processCode.id,
                                    query: `?reportId=${response.id}`
                                }).then(resp => {
                                    next(vm => {
                                        vm.redirectedFrom = from.name
                                        vm.taskNames = responses[0].data.results
                                        vm.report = response
                                        if (!vm.isReportFinished) vm.setDuration()
                                        vm.report.reportAmount = vm.isReportStopped || vm.isReportFinished ? response.reportAmount : response.leftAmount
                                        vm.machineStuffAmount = 1
                                        vm.maxReportAmount = response.leftAmount
                                        vm.taskName = vm.taskNames.find(item => item.id === response.objectType).name
                                        vm.routeParentName = to.matched.slice(-1)[0]?.parent.name
                                        vm.remainingTime = response.remainingTime
                                        vm.currentlyReportedQuantity = response.reportAmount
                                        vm.orderNumber = response.objectNumber
                                        vm.allowsSendingOrders = workspace ? workspace.allowsSendingOrders : false
                                        vm.allowsReportExcessAmount = workspace ? workspace.allowsReportExcessAmount : false
                                        vm.couriers = responses[1].data.results
                                        vm.workspaceId = workspace ? workspace.workspaceId : null
                                        vm.additionalActionsReportProcessCodeProductionSite = resp.data.results.slice(resp.data.results)
                                        vm.carouselIndexes = new Array(groupBy(vm.report.previews, 'type').length).fill(0)
                                        vm.carouselMockupIndexes = new Array(groupBy(vm.report.previews, 'type').length).fill(0)
                                    })
                                })
                            }
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                }
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    beforeRouteUpdate(to, from, next) {
        Promise.all([
            store.dispatch('reports/getTaskNames'),
            store.dispatch('shipments/sendings/getCouriers')
        ])
            .then(responses => {
                if (typeof to.params.report !== 'undefined') {
                    this.taskNames = responses[0].data.results
                    this.report = to.params.report
                    this.delay = 0
                    this.timeWithoutLimit = 0
                    clearInterval(this.intervalId)
                    if (!this.isReportFinished) this.setDuration()
                    next()
                } else {
                    store.dispatch('reports/getReport', to.params.id)
                        .then(response => {
                            this.taskNames = responses[0].data.results
                            this.report = response
                            this.delay = 0
                            this.timeWithoutLimit = 0
                            clearInterval(this.intervalId)
                            if (!this.isReportFinished) this.setDuration()
                            next()
                        })
                        .catch(error => {
                            next(error)
                            errorHandler(error)
                        })
                }
            })
            .catch(error => {
                next(error)
                errorHandler(error)
            })
    },
    data() {
        return {
            reportAmountDialog: false,
            stepper: 1,
            orderLoading: false,
            taskName: null,
            report: {
                statusId: null,
                objectDetail: {
                    reportItemPlateParameters: []
                },
                comments: [],
                nextScheduledTask: null
            },
            productionSiteData: null,
            maxReportAmount: null,
            redirectedFrom: null,
            stopTrackingLoading: false,
            duration: null,
            intervalId: null,
            routeParentName: null,
            machineStuffAmount: null,
            codeTypeId: null,
            delay: 0,
            timeWithoutLimit: 0,
            remainingTime: null,
            statusId: null,
            currentPaginationPage: null,
            taskNames: [],
            currentlyReportedQuantity: null,
            allowsSendingOrders: false,
            allowsReportExcessAmount: false,
            workspaceId: null,
            couriers: [],
            selectAll: true,
            selectAllReportProcessCode: true,
            additionalActionsProcessProductionSite: [],
            additionalActionsReportProcessCodeProductionSite: [],
            carouselIndexes: [],
            carouselMockupsIndexes: [],
            workspaceAccidentsListDialog: false,
            unsolvedAccidents: [],
            imagePreviewDialog: false,
            filePreviewURI: null,
            isPalletNumberRequired: false,
            controlCodeDialog: false,
            codeCompliancesListLoading: false,
            controlCodeList: [],
            additionalActionsProcessProductionSiteHeaders: [
                {
                    text: 'Wykonane czynności',
                    value: 'name',
                    sortable: false
                }
            ],
            reportCommentHeaders: [
                {
                    text: 'Zdarzenie',
                    value: 'codeType',
                    sortable: false,
                },
                {
                    text: 'Opis',
                    value: 'description',
                    sortable: false,
                    align: 'center',
                },
                {
                    text: 'Data utworzenia',
                    value: 'createdDateTime',
                    sortable: false,
                    align: 'center',
                },
            ],
            controlCodeHeaders: [
                {
                    text: 'Kod',
                    value: 'referentialPattern',
                    align: 'left',
                    sortable: false,
                },
                {
                    text: 'Zgodność kodu',
                    value: 'compliantCodesSum',
                    align: 'center',
                    sortable: false,
                },
                {
                    text: 'Niezgodność kodu',
                    value: 'nonCompliantCodesSum',
                    align: 'center',
                    sortable: false,
                },
            ],
        }
    },
    computed: {
        reportPreviews() {
            if (!this.hasPreviews) return 

            return groupBy(this.report.previews, 'type')
        },
        printMethod() {
            if (!this.report.objectDetail || !this.report.objectDetail.reportItemPlateParameters || !this.isOffsetPrintSite) return 

            const parameter = this.report.objectDetail.reportItemPlateParameters.find(
                (param) => param.name.toLowerCase() === 'sposób druku'
            )

            return parameter ? parameter.value : 'Nieokreślony'
        },
        isJob() {
            if (!this.report.objectDetail) return

            const allowedTypes = [10, 20, 30, 70, 80]
            const type = this.report.objectDetail.reportItemType

            return allowedTypes.includes(type)
        },

        getStep() {
            return this.isJob ? 3 : 2
        },
        nextScheduledTaskList() {
            return this.report.nextScheduledTask ? [this.report.nextScheduledTask] : []
        },
        isSendingsViewActive() {
            return this.stepper === 2
        },
        jobFromSchedule() {
            return this.report.objectDetail.reportItemType === 20 // PM_JOB
        },
        jobFactoryBase() {
            return this.report.objectDetail.reportItemType === 30 // FB_JOB
        },
        isOrder() {
            return this.report.objectType === 2
        },
        isReportFinished() {
            if (this.report.statusId) {
                return this.report.statusId == 99
            }

            return false
        },
        isReportStopped() {
            if (this.report.statusId) {
                return this.report.statusId == 90
            }

            return false
        },
        isProcessReport() {
            return this.report.objectType === 3
        },
        workspaceSettings() {
            const workspace = localStorage.getItem('workspace')

            return JSON.parse(workspace)
        },
        isOffsetPrintSite() {
            if (!this.workspaceSettings.workspaceTypeId) return
            
            return this.workspaceSettings.workspaceTypeId === 7
        },
        isFromReporting() {
            return this.routeParentName === 'reportsReporting'
        },
        isTypeIdValid() {
            return [15, 40, 50, 60].includes(this.report.objectDetail.reportItemType) // orders only
        },
        reportSettings() {
            return this.$store.state.reports.reporting.reportSettings
        },
        hasPreviews() {
            return this.report.previews && this.report.previews.length > 0
        },
        hasMockupPreviews() {
            return this.report.mockupPreviews && this.report.mockupPreviews.length > 0
        },
        previews() {
            return groupBy(this.report.previews, 'type')
        },
        mockupPreviews() {
            return groupBy(this.report.mockupPreviews, 'type')
        },
        unsolvedAccidentsAmount() {
            return this.unsolvedAccidents.length.toString()
        },
    },
    watch: {
        addressCarouselIndex: {
            handler: function(index) {
                this.currentAddress = this.order.addresses[index]
            }
        },
    },
    mounted() {
        this.getWorkspaceAccidentsList()
        this.getCodeCompliancesList()
    },
    destroyed() {
        clearInterval(this.intervalId)
    },
    methods: {
        prev(index, carouselMockup=false, number=4) {
            if (!carouselMockup) {
                return this.carouselIndexes.splice(index, 1, this.carouselIndexes[index] - 1)
            } else {
                return this.carouselMockupsIndexes.splice(index, 1, this.carouselMockupsIndexes[index] - number)
            }
        },
        next(index, carouselMockup=false, number=4) {
            if (!carouselMockup) {
                return this.carouselIndexes.splice(index, 1, this.carouselIndexes[index] + 1)
            } else {
                return this.carouselMockupsIndexes.splice(index, 1, this.carouselMockupsIndexes[index] + number)
            }
        },
        taskPreviewURI(image) {
            const mediaRoot = apiConfig.FACTORYBASE_API_URI

            return `${mediaRoot}${image.path}`
        },
        showControlCodeDialog() {
            this.controlCodeDialog = true
        },
        closeControlCodeDialog() {
            this.controlCodeDialog = false
            this.getCodeCompliancesList()
        },
        showFilePreviewDialog(image) {
            this.filePreviewURI = this.taskPreviewURI(image)
            this.imagePreviewDialog = true
        },
        closeImagePreviewDialog() {
            this.imagePreviewDialog = false
        },
        updateSelectAll() {
            this.selectAll = this.additionalActionsProcessProductionSite.filter(action =>
                action.isSelected === true).length === this.additionalActionsProcessProductionSite.length
        },
        toggleSelectAll(selectAllCheckbox) {
            this.selectAll = selectAllCheckbox
            this.additionalActionsProcessProductionSite.forEach(item => {
                item.isSelected = this.selectAll ? true : false
            })
        },
        updateSelectAllReportProcessCode() {
            this.selectAllReportProcessCode = this.additionalActionsReportProcessCodeProductionSite.filter(action =>
                action.isSelected === true).length === this.additionalActionsReportProcessCodeProductionSite.length
        },
        toggleSelectAllReportProcessCode(selectAllCheckbox) {
            this.selectAllReportProcessCode = selectAllCheckbox
            this.additionalActionsReportProcessCodeProductionSite.forEach(item => {
                item.isSelected = this.selectAllReportProcessCode ? true : false
            })
        },
        showWorkspaceAccidentsListDialog() {
            this.getWorkspaceAccidentsList()
            this.workspaceAccidentsListDialog = true
        },
        closeWorkspaceAccidentsListDialog() {
            this.workspaceAccidentsListDialog = false
        },
        getWorkspaceAccidentsList() {
            this.$store.dispatch('reports/getUnsolvedMachineAccidents', this.report.machine)
                .then(response => {
                    this.unsolvedAccidents = response.data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
        },
        getCodeCompliancesList() {
            this.codeCompliancesListLoading = true
            this.$store.dispatch('reports/getCodeCompliancesList', {
                query: `?report__id=${this.report.id}&orderNumber=${this.report.objectNumber}`,
            })
                .then(({data}) => {
                    this.controlCodeList = data.results
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.codeCompliancesListLoading = false
                })
        },
        showNextStep() {
            this.stepper += 1

            if (this.isJob) return

            EventBus.$emit('getOrder', this.report.objectNumber)

        },
        showPreviousStep() {
            this.stepper -= 1
        },
        setOrderLoading(value) {
            this.orderLoading = value
        },
        setDuration() {
            const timeElapsed = Math.floor((this.report.remainingTime) / 1000)

            this.report.remainingTime == null ? this.remainingTime = 0 : this.remainingTime = this.report.remainingTime

            if (timeElapsed >= this.remainingTime) { // przekroczono czas wykonania = jest opoznienie lub nie ma limitu
                this.report.remainingTime == null ? this.timeWithoutLimit = timeElapsed : this.delay = Math.floor(timeElapsed - this.remainingTime)
                this.duration = -1
            }
            if (timeElapsed < this.remainingTime) { // miescimy sie w czasie
                this.duration = Math.floor(this.remainingTime - timeElapsed)
            }
            if (this.report.statusId === 1) {
                this.refreshDuration()
                this.intervalId = setInterval(this.refreshDuration, 1000)
            }
        },
        refreshDuration() {
            if (this.duration >= 0) {
                this.duration -= 1
            }
            if (this.duration == -1) {
                this.report.remainingTime == null ? this.timeWithoutLimit += 1 : this.delay += 1
            }
        },
        stopTracking() {
            this.finishReport()
        },
        finishReport() {
            this.stopTrackingLoading = true

            this.$store.dispatch('reports/finishReport', {
                id: this.report.id,
                payload: {
                    objectId: this.report.objectId,
                    objectType: this.report.objectType,
                    objectNumber: this.report.objectNumber,
                    machine: this.report.machine,
                    process: this.report.process,
                    user: this.report.user,
                    reportAmount: this.report.reportAmount,
                    machineStuffAmount: this.machineStuffAmount,
                    comments: this.report.comments,
                    statusId: 99,
                    currentlyReportedQuantity: 0,
                    productionSiteId: this.report.productionSiteId,
                    additionalActionProcessProductionSites: JSON.parse(JSON.stringify(this.additionalActionsProcessProductionSite)),
                    additionalActionReportProcessCode: JSON.parse(JSON.stringify(this.additionalActionsReportProcessCodeProductionSite)),
                }
            })
                .then(response => {
                    if (
                        this.isFromReporting &&
                        response.data.objectType === 1 && // Zlecenie produkcyjne
                        response.data.leftAmount <= 0
                    ) {
                        this.$store.dispatch('reports/findTask', {
                            taskNumber: this.report.objectNumber,
                            productionProcessId: this.report.process
                        })
                            .then(response => {
                                this.handleUnreportedPlates(response.data.results[0].reportItemPlates)
                            })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.stopTrackingLoading = false
                            })
                    } else {
                        this.$router.push({ name: this.routeParentName })
                            .catch(error => {
                                errorHandler(error)
                            })
                            .finally(() => {
                                this.stopTrackingLoading = false
                            })
                    }
                })
                .catch(error => {
                    errorHandler(error)
                    this.stopTrackingLoading = false
                })
        },
        updateRenewReport(reportData) {
            this.report = reportData
            if (!this.isReportFinished) this.setDuration()
            this.machineStuffAmount = reportData.machineStuffAmount
            this.report.reportAmount = reportData.leftAmount
            this.maxReportAmount = reportData.leftAmount
            this.remainingTime = reportData.remainingTime
        },
        updateEndMakeReadyReport(reportData) {
            this.report = reportData
            this.machineStuffAmount = reportData.machineStuffAmount
            this.report.reportAmount = reportData.leftAmount
            this.maxReportAmount = reportData.leftAmount
            this.remainingTime = reportData.remainingTime
            this.delay = 0
            clearInterval(this.intervalId)
            this.setDuration()
        },
        close() {
            if (this.redirectedFrom === 'reportsReportsShiftSummariesDetailedReports' && this.report.reportShiftSummaryId) {
                this.$router.push({
                    name: 'reportsReportsShiftSummariesDetailedReports',
                    params: {
                        id: this.report.reportShiftSummaryId
                    }
                })
            } else {
                this.$router.push({ name: this.routeParentName })
                    .catch(error => {
                        errorHandler(error)
                    })
            }
        },
        closeReportAmountDialog() {
            this.reportAmountDialog = false

            setTimeout(() => {
                this.singleReport = {}
            }, 500)
        },
        showEditReportAmountDialog() {
            this.reportAmountDialog = true
        },
        saveReportAmountData(reportData) {
            this.closeReportAmountDialog()
            this.report = reportData
        },
    },
}
</script>
